<template>
  <div class="space-y-2.5 p-3 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg">
    <div class="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
      <!-- Component: search this org's own IT components. -->
      <div>
        <label class="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Component<span class="text-red-500">*</span></label>
        <div class="relative">
          <div ref="componentFieldWrapper"
            :class="[error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700',
                     componentOpen ? 'rounded-t-md border-b-transparent bg-gray-50 dark:bg-white/[0.04]' : 'rounded-md bg-white dark:bg-[#0d1117]']"
            class="w-full h-[31px] flex items-center gap-1.5 pl-1.5 pr-1.5 border">
            <ItComponentLogo v-if="selectedComponent" :name="selectedComponent.name" :logo-url="selectedComponent.logo_url" size="w-4 h-4" initial-text-size="text-[8px]" image-padding="p-0.5" class="flex-shrink-0" />
            <input ref="componentInput" v-model="componentQuery" type="text" placeholder="Search this org's IT components…" autocomplete="off"
              @focus="openComponentDropdown" @input="handleComponentTyped" @keydown="handleComponentKeydown"
              class="flex-1 min-w-0 text-sm leading-4 bg-transparent border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 placeholder:text-xs focus:outline-none focus:ring-0" />
            <svg class="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform" :class="componentOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>

          <Teleport to="body">
            <div v-if="componentOpen" class="fixed inset-0 z-[9998]" @click="closeComponentDropdown"></div>
            <div v-if="componentOpen" :style="componentPanelStyle"
              class="fixed z-[9999] bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-md shadow-lg py-1.5 max-h-72 overflow-y-auto">
              <button v-for="(component, index) in componentSuggestions" :key="component.id" type="button"
                :ref="(el) => setComponentSuggestionRef(el, index)"
                @click="selectComponent(component)" @mouseenter="activeComponentIndex = index"
                :class="index === activeComponentIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
                class="w-full flex items-center gap-2.5 text-left px-3.5 py-2 transition-colors">
                <ItComponentLogo :name="component.name" :logo-url="component.logo_url" size="w-7 h-7" initial-text-size="text-[10px]" />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ component.name }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ typeLabel(component.type) }}<span v-if="component.vendor"> · {{ component.vendor }}</span></p>
                </div>
              </button>
              <p v-if="componentSuggestions.length === 0" class="px-3.5 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
                No matching IT components. Ask your admin to add it if it's not available.
              </p>
            </div>
          </Teleport>
        </div>
      </div>

      <!-- Version: only this component's own already-tracked versions —
           nothing is typed or created here, see OrgComponentVersionsSection
           for adding a new tracked version. -->
      <div>
        <label class="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Version<span class="text-red-500">*</span></label>
        <div class="relative">
          <div ref="versionFieldWrapper"
            :class="[!selectedComponentId ? 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 cursor-not-allowed' : (versionOpen ? 'rounded-t-md border-b-transparent bg-gray-50 dark:bg-white/[0.04] border-gray-200 dark:border-gray-700' : 'rounded-md bg-white dark:bg-[#0d1117] border-gray-200 dark:border-gray-700')]"
            class="w-full h-[31px] flex items-center gap-1.5 pl-2.5 pr-1.5 border">
            <input ref="versionInput" v-model="versionQuery" type="text" :placeholder="versionPlaceholder" autocomplete="off" :disabled="!selectedComponentId"
              @focus="openVersionDropdown" @input="handleVersionTyped" @keydown="handleVersionKeydown"
              class="flex-1 min-w-0 text-sm leading-4 bg-transparent border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 placeholder:text-xs focus:outline-none focus:ring-0 disabled:cursor-not-allowed" />
            <!-- Mirrors the same cue on the suggestion row below — still
                 visible once picked, not just while browsing the list. -->
            <span v-if="!versionOpen && selectedVersion && isAlreadyAdded(selectedVersion)"
              class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
              Already added
            </span>
            <svg v-if="selectedComponentId" class="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform" :class="versionOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>

          <Teleport to="body">
            <div v-if="versionOpen" class="fixed inset-0 z-[9998]" @click="closeVersionDropdown"></div>
            <div v-if="versionOpen" :style="versionPanelStyle"
              class="fixed z-[9999] bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-md shadow-lg py-1.5 max-h-72 overflow-y-auto">
              <button v-for="(version, index) in versionSuggestions" :key="version.id" type="button"
                :ref="(el) => setVersionSuggestionRef(el, index)"
                @click="selectVersion(version)" @mouseenter="activeVersionIndex = index"
                :class="index === activeVersionIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
                class="w-full flex items-center justify-between gap-2 text-left px-3.5 py-2 transition-colors">
                <div class="min-w-0">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {{ version.version }}<span v-if="version.alias" class="text-gray-400 dark:text-gray-500 font-normal"> · {{ version.alias }}</span>
                  </p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 truncate">
                    {{ version.released_date ? formatDate(version.released_date) : 'Release date not specified' }}
                  </p>
                </div>
                <!-- Already a dependency of this application — still
                     selectable (matches OrgComponentVersionForm's own
                     "Already added" cue), just flagged so re-picking it is a
                     deliberate choice rather than an accidental duplicate. -->
                <span v-if="isAlreadyAdded(version)" class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                  Already added
                </span>
              </button>
              <p v-if="versionsLoading" class="px-3.5 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">Loading versions…</p>
              <p v-else-if="versionSuggestions.length === 0 && componentVersions.length === 0" class="px-3.5 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
                No tracked versions yet. Ask your administrator to add one from the IT Components page.
              </p>
              <p v-else-if="versionSuggestions.length === 0" class="px-3.5 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
                No matching version. Ask your administrator to add it from the IT Components page.
              </p>
            </div>
          </Teleport>
        </div>
      </div>

      <!-- Alias/Released/End of life: read only, derived from the selected
           version's own tracked data — not editable here. To change these,
           edit the version itself from the IT Components page's own versions
           manager (see OrgComponentVersionForm.vue). -->
      <div>
        <label class="flex items-center justify-between mb-1">
          <span class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Alias</span>
          <span class="relative group/alias-tip flex-shrink-0">
            <svg class="w-3 h-3 text-gray-300 dark:text-gray-600 cursor-help" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
            </svg>
            <span class="pointer-events-none absolute right-0 bottom-full mb-1.5 z-50 w-40 opacity-0 scale-95 group-hover/alias-tip:opacity-100 group-hover/alias-tip:scale-100 transition-all duration-150 origin-bottom-right">
              <span class="block rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1.5 text-[10px] leading-snug font-medium text-white shadow-lg text-center">
                Only an administrator can update this from the IT Components page. It'll update here automatically.
              </span>
            </span>
          </span>
        </label>
        <input type="text" :value="aliasDisplay" disabled readonly
          class="w-full h-[31px] px-2.5 text-sm border rounded-md bg-gray-50 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500 border-gray-100 dark:border-gray-800 cursor-not-allowed" />
      </div>

      <div>
        <label class="flex items-center justify-between mb-1">
          <span class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Released</span>
          <span class="relative group/released-tip flex-shrink-0">
            <svg class="w-3 h-3 text-gray-300 dark:text-gray-600 cursor-help" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
            </svg>
            <span class="pointer-events-none absolute right-0 bottom-full mb-1.5 z-50 w-40 opacity-0 scale-95 group-hover/released-tip:opacity-100 group-hover/released-tip:scale-100 transition-all duration-150 origin-bottom-right">
              <span class="block rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1.5 text-[10px] leading-snug font-medium text-white shadow-lg text-center">
                Only an administrator can update this from the IT Components page. It'll update here automatically.
              </span>
            </span>
          </span>
        </label>
        <input type="text" :value="releasedDisplay" disabled readonly
          class="w-full h-[31px] px-2.5 text-sm border rounded-md bg-gray-50 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500 border-gray-100 dark:border-gray-800 cursor-not-allowed" />
      </div>

      <div>
        <label class="flex items-center justify-between mb-1">
          <span class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">End of life</span>
          <span class="relative group/eol-tip flex-shrink-0">
            <svg class="w-3 h-3 text-gray-300 dark:text-gray-600 cursor-help" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
            </svg>
            <span class="pointer-events-none absolute right-0 bottom-full mb-1.5 z-50 w-40 opacity-0 scale-95 group-hover/eol-tip:opacity-100 group-hover/eol-tip:scale-100 transition-all duration-150 origin-bottom-right">
              <span class="block rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1.5 text-[10px] leading-snug font-medium text-white shadow-lg text-center">
                Only an administrator can update this from the IT Components page. It'll update here automatically.
              </span>
            </span>
          </span>
        </label>
        <input type="text" :value="eolDisplay" disabled readonly
          class="w-full h-[31px] px-2.5 text-sm border rounded-md bg-gray-50 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500 border-gray-100 dark:border-gray-800 cursor-not-allowed" />
      </div>
    </div>

    <p v-if="error" class="text-[11px] text-red-500 dark:text-red-400 font-medium">{{ error }}</p>

    <div class="flex gap-2 justify-end">
      <button type="button" @click="$emit('cancel')"
        class="px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md cursor-pointer">
        Cancel
      </button>
      <button type="button" @click="submit" :disabled="saving || !selectedVersionId"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-md cursor-pointer">
        <svg v-if="saving" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        {{ submitLabel }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { typeLabel } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import { useOrgComponentOptionsStore } from 'stores/orgComponentOptions.js'
import { useOrgComponentVersionsStore } from 'stores/orgComponentVersions.js'

const componentOptionsStore = useOrgComponentOptionsStore()
const versionsStore = useOrgComponentVersionsStore()

const props = defineProps({
  organizationId: { type: String, required: true },
  submitLabel: { type: String, default: 'Attach' },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
  // Present (and pre-selected) when editing an existing dependency.
  initialOrgComponentId: { type: String, default: '' },
  initialOrgComponentVersionId: { type: String, default: '' },
  // org_component_version_ids this application already depends on (across
  // every component, not just the one currently selected) — flags a
  // suggestion as "Already added" the same way OrgComponentVersionForm's own
  // existingVersions does. Excludes the dependency being edited, if any, so
  // editing a row doesn't flag its own current version.
  existingDependencyVersionIds: { type: Array, default: () => [] },
})

const emit = defineEmits(['cancel', 'submit'])

const componentFieldWrapper = ref(null)
const componentInput = ref(null)
const versionFieldWrapper = ref(null)
const versionInput = ref(null)

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Component picker ──────────────────────────────────────────────────────────
const selectedComponentId = ref(props.initialOrgComponentId || null)
const componentQuery = ref('')
const componentOpen = ref(false)
const componentPanelStyle = ref({})
const activeComponentIndex = ref(-1)
const componentSuggestionRefs = ref([])

const selectedComponent = computed(() =>
  componentOptionsStore.allComponents.find((c) => c.id === selectedComponentId.value) || null)

const componentSuggestions = computed(() => {
  const query = componentQuery.value.trim().toLowerCase()
  const all = componentOptionsStore.allComponents
  return (!query ? all.slice(0, 20) : all.filter((c) => c.name.toLowerCase().includes(query)).slice(0, 8))
})

watch(componentSuggestions, () => {
  activeComponentIndex.value = -1
  componentSuggestionRefs.value = []
})
watch(activeComponentIndex, (i) => { if (i >= 0) componentSuggestionRefs.value[i]?.scrollIntoView({ block: 'nearest' }) })
function setComponentSuggestionRef(el, i) { if (el) componentSuggestionRefs.value[i] = el }

function positionPanel(wrapperEl) {
  const rect = wrapperEl.getBoundingClientRect()
  return { top: `${rect.bottom}px`, left: `${rect.left}px`, width: `${rect.width}px` }
}

function closeComponentDropdown() {
  componentOpen.value = false
  activeComponentIndex.value = -1
  componentQuery.value = selectedComponent.value ? selectedComponent.value.name : ''
}

function openComponentDropdown() {
  componentPanelStyle.value = positionPanel(componentFieldWrapper.value)
  componentOpen.value = true
  activeComponentIndex.value = -1
  nextTick(() => { componentInput.value?.focus(); componentInput.value?.select() })
}

function handleComponentTyped() {
  if (selectedComponent.value && componentQuery.value !== selectedComponent.value.name) {
    selectComponentId(null)
  }
  if (!componentOpen.value) openComponentDropdown()
}

function selectComponentId(id) {
  selectedComponentId.value = id
  selectedVersionId.value = null
  versionQuery.value = ''
}

function selectComponent(component) {
  selectComponentId(component.id)
  closeComponentDropdown()
  versionsStore.fetchIfNeeded(props.organizationId, component.id)
  nextTick(() => versionInput.value?.focus())
}

function handleComponentKeydown(e) {
  if (!componentOpen.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); openComponentDropdown() }
    return
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (componentSuggestions.value.length) activeComponentIndex.value = (activeComponentIndex.value + 1) % componentSuggestions.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (componentSuggestions.value.length) {
      activeComponentIndex.value = activeComponentIndex.value <= 0 ? componentSuggestions.value.length - 1 : activeComponentIndex.value - 1
    }
  } else if (e.key === 'Escape') {
    closeComponentDropdown()
  } else if (e.key === 'Enter' && activeComponentIndex.value >= 0) {
    e.preventDefault()
    selectComponent(componentSuggestions.value[activeComponentIndex.value])
  }
}

// ── Version picker (scoped to the selected component) ────────────────────────
const selectedVersionId = ref(props.initialOrgComponentVersionId || null)
const versionQuery = ref('')
const versionOpen = ref(false)
const versionPanelStyle = ref({})
const activeVersionIndex = ref(-1)
const versionSuggestionRefs = ref([])

const versionEntry = computed(() => (selectedComponentId.value ? versionsStore.entryFor(selectedComponentId.value) : null))
const componentVersions = computed(() => versionEntry.value?.records || [])
const versionsLoading = computed(() => !!versionEntry.value?.loading)

const selectedVersion = computed(() =>
  componentVersions.value.find((v) => v.id === selectedVersionId.value) || null)

// Read-only display for Alias/Released/End of life — these belong to the
// tracked version itself, not to this attach form, so they're never editable
// here (see OrgComponentVersionForm.vue for actually changing them).
const aliasDisplay = computed(() => selectedVersion.value?.alias || 'Not specified')

const releasedDisplay = computed(() =>
  selectedVersion.value?.released_date ? formatDate(selectedVersion.value.released_date) : 'Not specified')

const eolDisplay = computed(() => {
  if (!selectedVersion.value) return 'Not specified'
  if (selectedVersion.value.eol_date) return formatDate(selectedVersion.value.eol_date)
  return selectedVersion.value.eol_date_not_announced ? 'Not announced' : 'Not specified'
})

const versionPlaceholder = computed(() => (selectedComponentId.value ? 'Search versions…' : 'Pick a component first'))

const versionSuggestions = computed(() => {
  const query = versionQuery.value.trim().toLowerCase()
  const all = componentVersions.value
  if (!query) return all.slice(0, 20)
  return all.filter((v) => v.version.toLowerCase().includes(query) || (v.alias || '').toLowerCase().includes(query))
           .slice(0, 8)
})

watch(versionSuggestions, () => {
  activeVersionIndex.value = -1
  versionSuggestionRefs.value = []
})
watch(activeVersionIndex, (i) => { if (i >= 0) versionSuggestionRefs.value[i]?.scrollIntoView({ block: 'nearest' }) })
function setVersionSuggestionRef(el, i) { if (el) versionSuggestionRefs.value[i] = el }

function closeVersionDropdown() {
  versionOpen.value = false
  activeVersionIndex.value = -1
  versionQuery.value = selectedVersion.value ? selectedVersion.value.version : ''
}

function openVersionDropdown() {
  if (!selectedComponentId.value) return
  versionPanelStyle.value = positionPanel(versionFieldWrapper.value)
  versionOpen.value = true
  activeVersionIndex.value = -1
}

function handleVersionTyped() {
  if (selectedVersion.value && versionQuery.value !== selectedVersion.value.version) {
    selectedVersionId.value = null
  }
  if (!versionOpen.value) openVersionDropdown()
}

function selectVersion(version) {
  selectedVersionId.value = version.id
  closeVersionDropdown()
}

function isAlreadyAdded(version) {
  return props.existingDependencyVersionIds.includes(version.id)
}

function handleVersionKeydown(e) {
  if (!versionOpen.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); openVersionDropdown() }
    return
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (versionSuggestions.value.length) activeVersionIndex.value = (activeVersionIndex.value + 1) % versionSuggestions.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (versionSuggestions.value.length) {
      activeVersionIndex.value = activeVersionIndex.value <= 0 ? versionSuggestions.value.length - 1 : activeVersionIndex.value - 1
    }
  } else if (e.key === 'Escape') {
    closeVersionDropdown()
  } else if (e.key === 'Enter' && activeVersionIndex.value >= 0) {
    e.preventDefault()
    selectVersion(versionSuggestions.value[activeVersionIndex.value])
  }
}

function submit() {
  if (!selectedVersionId.value) return
  emit('submit', { org_component_version_id: selectedVersionId.value })
}

onMounted(() => {
  componentOptionsStore.fetchAllIfNeeded(props.organizationId)

  if (props.initialOrgComponentId) {
    versionsStore.fetchIfNeeded(props.organizationId, props.initialOrgComponentId)
    componentQuery.value = selectedComponent.value?.name || ''
    versionQuery.value = selectedVersion.value?.version || ''
  } else {
    // A truly blank Attach opens with recommendations showing and the search
    // box focused right away, instead of requiring an extra click.
    openComponentDropdown()
  }
})

// The component list may still be loading when initialOrgComponentId is set
// (edit mode) — once it resolves, backfill the display name.
watch(() => componentOptionsStore.allComponents, () => {
  if (props.initialOrgComponentId && !componentQuery.value && selectedComponent.value) {
    componentQuery.value = selectedComponent.value.name
  }
})

watch(() => versionEntry.value?.records, () => {
  if (props.initialOrgComponentVersionId && !versionQuery.value && selectedVersion.value) {
    versionQuery.value = selectedVersion.value.version
  }
})
</script>
